import { styled } from '@mui/material';

import { Button } from '../../atoms/Button';
import { Stack } from '../../atoms/Stack';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';
import { HTMLDisplay } from '../HTMLDisplay';
import { Dialog } from '../../organisms/Dialog';
import { CommunicationDocuments, CommunicationPeople } from './CommunicationDetails';
import { type CommunicationPreviewProps, useCommunicationPreview } from './useCommunicationPreview';

type Props = CommunicationPreviewProps;

type PreviewOwnerState = {
  maxLines: number;
};

// Same reading as an inbox row: secondary body text, flattened and clamped.
const PreviewText = styled('p')<{ ownerState: PreviewOwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.body2,
  margin: 0,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: ownerState.maxLines,
  WebkitBoxOrient: 'vertical',
  overflowWrap: 'anywhere',
}));

const FullText = styled('div')(({ theme }) => ({
  ...theme.typography.body1,
  maxWidth: '75ch',
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
}));

/**
 * Comment of a communication (e-mail, call, reminder…) as a clamped preview.
 * When the preview is cut, a text button opens the whole message, line breaks kept, in a dialog.
 */
export function CommunicationPreview(props: Props) {
  const hook = useCommunicationPreview(props);
  const Icon = hook.IconComponent;

  if (!hook.hasComment) {
    return null;
  }

  return (
    <>
      <Stack spacing={0.5} alignItems="flex-start" data-testid={props['data-testid']}>
        <PreviewText ref={hook.previewRef} ownerState={{ maxLines: hook.maxLines }}>
          {hook.commentPreview}
        </PreviewText>
        {hook.isPreviewTruncated ? (
          <Button
            variant="text"
            size="small"
            color="default"
            aria-haspopup="dialog"
            onClick={hook.handleOpenFullComment}
          >
            {hook.fullCommentLabel}
          </Button>
        ) : null}
      </Stack>

      <Dialog
        open={hook.isFullCommentOpen}
        onClose={hook.handleCloseFullComment}
        title={hook.dialogTitle}
        closeLabel="Fermer"
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
              {hook.isExchange ? (
                <Tag color={hook.tagColor} label={hook.typeLabel} icon={<Icon />} />
              ) : null}
              {hook.formattedDate ? (
                <Typography variant="caption">{hook.formattedDate}</Typography>
              ) : null}
            </Stack>
            <CommunicationPeople
              providerName={hook.providerName}
              contactName={hook.contactName}
              isExchange={hook.isExchange}
            />
          </Stack>

          {hook.isHtmlComment ? (
            <HTMLDisplay content={hook.comment} />
          ) : (
            <FullText>{hook.comment}</FullText>
          )}

          <CommunicationDocuments documents={hook.documents} />
        </Stack>
      </Dialog>
    </>
  );
}

export type { CommunicationPreviewProps };
